import ItemDetailCard from "@urbanpiper-engineering/meraki-components/dist/Molecules/ItemDetailCard";
import RecommendationCarousel from "@urbanpiper-engineering/meraki-components/dist/Cells/RecommendationCarousel";
import ItemCard from "@urbanpiper-engineering/meraki-components/dist/Molecules/ItemCard";
import {
  FloatingStack,
  Button,
  AddToCart,
} from "@urbanpiper-engineering/meraki-components";
import MerakiSDK from "@urbanpiper-engineering/meraki-sdk";
import { CartState } from "meraki-sdk/dist/esm/cart/states/cart.state";
import { getServerSideProps as getInitialProps } from "../../../hooks/server.props";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "next-i18next";
import styles from "./item.module.scss";
import { useRouter } from "next/router";
import ItemOptionsDialog from "../../../components/item_options";
import pageSelector from "../../../external/pageSelector";
import { onItemUpdate } from "../../../hooks/cart.util";
import useIframe from "../../../hooks/iframe.hook";
import ITEM_DATA from "../../../editor-static-data/item-details.json";
import SeoData from "../../../components/seo_data";
import Image from "next/image";
import { useIsMobile } from "../../../hooks/is_mobile.hook";

export const getServerSideProps = async (props: any) => {
  const { id } = props.query;
  const initProps = await getInitialProps(props);
  const pageConfig = pageSelector.getItemDetailPage();
  let _itemInfo: any = {};
  let seoData = null;
  let openGraphData = null;

  try {
    _itemInfo = await MerakiSDK.getItemById({
      item_id: Number(id),
    });
  } catch (error) {
    console.log(error, "itemInfoError");
  }

  try {
    let url = "";

    const protocol =
      props.req.headers["x-forwarded-proto"] || props.req.connection.encrypted
        ? "https"
        : "http";
    const host = props.req.headers.host;

    url = `${protocol}://${host}/api/v1/item/${_itemInfo?.id}/${_itemInfo?.slug}`;

    if (_itemInfo?.id) {
      seoData = {
        title: _itemInfo?.item_title || null,
        description: _itemInfo?.item_desc || null,
        keywords: _itemInfo?.item_title || null,
      };
    }
    openGraphData = {
      title: _itemInfo?.item_title || null,
      description: _itemInfo?.item_desc || null,
      url: url || null,
      image: _itemInfo?.image_url || null,
    };
  } catch (error) {
    console.log(error, "itemInfo seo error");
  }

  return {
    props: {
      ...initProps.props,
      _itemInfo,
      pageConfig,
      seoData,
      openGraphData,
    },
  };
};

function ItemDetail({
  _itemInfo,
  pageConfig,
  seoData,
  openGraphData,
}: {
  _itemInfo: any;
  pageConfig: any;
  seoData: any;
  openGraphData: any;
}) {
  const isMobile = useIsMobile();
  const [itemInfo, setItemInfo] = useState({});
  const router = useRouter();
  const { t } = useTranslation();
  const useCartState = MerakiSDK.getCartState().hook();
  const [favouritesList, setFavouritesList] = useState([]);
  const [cartItems, cartItemQty] = useCartState((state: typeof CartState) => [
    state.cart?.items || [],
    state.cart?.item_count,
  ]);
  const { loadedInIframe } = useIframe();

  useEffect(() => {
      (async () => {
        try {
          const newItemInfo = await MerakiSDK.getItemById({
            item_id: Number(_itemInfo?.id || router?.query?.id),
          });
          setItemInfo(newItemInfo);
        } catch (error) {
          console.log(error, "itemInfoError");
        }
      })();
  }, []);

  const cartItemQuantityMap = useMemo(
    () =>
      cartItems.reduce((acc: any, item: any) => {
        if (!acc[item.id]) {
          acc[item.id] = 0;
        }
        acc[item.id] += item.quantity;
        return acc;
      }, {}),
    [cartItems, cartItemQty]
  );

  const { recommendedItems: showRecommendedItems } = pageConfig.customizations;
  const [recommendedItems, setRecommendedItems] = React.useState([]);

  useEffect(() => {
    if (!itemInfo?.id) {
      if (loadedInIframe) {
        setItemInfo(ITEM_DATA.itemInfo);

        if (showRecommendedItems) {
          setRecommendedItems(ITEM_DATA.recommendedItems);
        } else {
          setRecommendedItems([]);
        }
      }
    } else {
      if (showRecommendedItems) {
        MerakiSDK.getRecommendedItems(itemInfo?.id).then((res: any) => {
          setRecommendedItems(res);
        });
      }
    }

    fetchFavourites();
    return () => {
      if (showRecommendedItems) {
        setRecommendedItems([]);
      }
    };
  }, [itemInfo, showRecommendedItems, loadedInIframe]);

  const fetchFavourites = () => {
    return MerakiSDK.getFavorites()
      .then((res: any) => {
        if (res?.status !== "error") {
          setFavouritesList(res?.map((item: any) => item.item.id));
        }
      })
      .catch((err: any) => {
        console.log(err, "err");
      });
  };

  const toggleFavourite = async (item: any, status: boolean) => {
    if (status) {
      await MerakiSDK.addItemAsFavourite(item.id);
    } else {
      await MerakiSDK.removeItemAsFavourite(item.id);
    }
  };

  return (
    <div className={styles.item}>
      <SeoData seoData={seoData} openGraphData={openGraphData} />
      <div className={`container`}>
        <ItemDetailCard
          item={itemInfo}
          count={cartItemQuantityMap[itemInfo?.id] || 0}
          onClickShare={() => {}}
          onItemUpdate={onItemUpdate}
          isFavourite={favouritesList?.includes(itemInfo?.id)}
          toggleFavourite={(status: boolean) => {
            toggleFavourite(itemInfo, status);
          }}
          imageComponent={Image}
        />
        {recommendedItems?.length > 0 && (
          <RecommendationCarousel itemlistLength={recommendedItems?.length}>
            {recommendedItems.map((item: any) => {
              return (
                <ItemCard
                  {...item}
                  count={cartItemQuantityMap[item.id] || 0}
                  key={item.id}
                  onItemUpdate={onItemUpdate}
                  onClickCallback={() => {
                    router.push(`/item/${item.id}/${item.slug}`);
                  }}
                  isFavourite={favouritesList?.includes(item?.id)}
                  isFavouriteEnabled
                  toggleFavourite={(status: boolean) => {
                    toggleFavourite(item, status);
                  }}
                />
              );
            })}
          </RecommendationCarousel>
        )}
        {isMobile && (
          <FloatingStack>
            <div className={styles.item_detail__customize_button}>
              {itemInfo?.has_options && !cartItemQuantityMap[itemInfo?.id] ? (
                <Button
                  size="lg"
                  variant="solid"
                  onClick={async () =>
                    await onItemUpdate({ ...itemInfo, quantity: 0 }, 1)
                  }
                >
                  {t("itemCard.customize")}
                </Button>
              ) : (
                <AddToCart
                  classes="item-detail-card__add-to-cart"
                  quantity={cartItemQuantityMap[itemInfo?.id] || 0}
                  loading={false}
                  stock={itemInfo?.current_stock}
                  variant="solid"
                  onItemUpdate={async (
                    id: number,
                    quantity: number,
                    dir: number
                  ) => {
                    onItemUpdate &&
                      (await onItemUpdate(
                        {
                          quantity,
                          id,
                          ...itemInfo,
                        },
                        dir
                      ));
                  }}
                  itemId={itemInfo?.id}
                />
              )}
            </div>
          </FloatingStack>
        )}
      </div>
    </div>
  );
}

export default ItemDetail;
